// ./src/utils/api/EmbeddingService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Venue } from "./VenueService";
import { Room } from "./RoomService";

// GraphQL queries
const EMBEDDING_LIST_QUERY = gql`
  query EmbeddingList($filter: EmbeddingFilter, $params: PaginationAndSorting) {
    embeddingList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        layoutType
        columnsDesktop
        columnsTablet
        calendarView
        calendarStartDay
        showWeekend
        calendarEventLimit
        eventTemplateId
        maxEvents
        showImages
        showDescriptions
        showLocation
        showRoom
        datetimeFormat
        themeId
        customCss
        accessToken
        allowedDomains
        lastAccessedAt
        totalRequests
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDING_GET_QUERY = gql`
  query EmbeddingGet($id: ID) {
    embeddingGet(id: $id) {
      code
      data {
        id
        accountId
        name
        description
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        layoutType
        columnsDesktop
        columnsTablet
        calendarView
        calendarStartDay
        showWeekend
        calendarEventLimit
        eventTemplateId
        maxEvents
        showImages
        showDescriptions
        showLocation
        showRoom
        datetimeFormat
        themeId
        customCss
        accessToken
        allowedDomains
        lastAccessedAt
        totalRequests
        createdAt
        updatedAt
        createdBy
        updatedBy
        venue {
          id
          name
          description
          status
        }
        room {
          id
          venueId
          name
          description
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDING_GET_MANY_QUERY = gql`
  query EmbeddingGetMany($ids: [ID]) {
    embeddingGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        description
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        layoutType
        columnsDesktop
        columnsTablet
        calendarView
        calendarStartDay
        showWeekend
        calendarEventLimit
        eventTemplateId
        maxEvents
        showImages
        showDescriptions
        showLocation
        showRoom
        datetimeFormat
        themeId
        customCss
        accessToken
        allowedDomains
        lastAccessedAt
        totalRequests
        createdAt
        updatedAt
        createdBy
        updatedBy
        venue {
          id
          name
          description
          status
        }
        room {
          id
          venueId
          name
          description
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EMBEDDING_CREATE_MUTATION = gql`
  mutation EmbeddingCreate($params: EmbeddingInput) {
    embeddingCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        description
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        layoutType
        columnsDesktop
        columnsTablet
        calendarView
        calendarStartDay
        showWeekend
        calendarEventLimit
        eventTemplateId
        maxEvents
        showImages
        showDescriptions
        showLocation
        showRoom
        datetimeFormat
        themeId
        customCss
        accessToken
        allowedDomains
        lastAccessedAt
        totalRequests
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDING_UPDATE_MUTATION = gql`
  mutation EmbeddingUpdate(
    $where: EmbeddingWhereInput
    $params: EmbeddingInput
  ) {
    embeddingUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        layoutType
        columnsDesktop
        columnsTablet
        calendarView
        calendarStartDay
        showWeekend
        calendarEventLimit
        eventTemplateId
        maxEvents
        showImages
        showDescriptions
        showLocation
        showRoom
        datetimeFormat
        themeId
        customCss
        accessToken
        allowedDomains
        lastAccessedAt
        totalRequests
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDING_REMOVE_MUTATION = gql`
  mutation EmbeddingRemove($where: EmbeddingWhereInput) {
    embeddingRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDING_REMOVE_MANY_MUTATION = gql`
  mutation EmbeddingRemoveMany($where: [EmbeddingWhereInput]) {
    embeddingRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Embedding {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  scheduleType?: string | undefined;
  venueId?: string | undefined;
  roomId?: string | undefined;
  roomTypeId?: string | undefined;
  floor?: string | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  showPastHours?: number | null | undefined;
  showUpcomingHours?: number | null | undefined;
  layoutType?: string | undefined;
  columnsDesktop?: number | null | undefined;
  columnsTablet?: number | null | undefined;
  calendarView?: string | undefined;
  calendarStartDay?: number | null | undefined;
  showWeekend?: boolean | null | undefined;
  calendarEventLimit?: number | null | undefined;
  eventTemplateId?: string | undefined;
  maxEvents?: number | null | undefined;
  showImages?: boolean | null | undefined;
  showDescriptions?: boolean | null | undefined;
  showLocation?: boolean | null | undefined;
  showRoom?: boolean | null | undefined;
  datetimeFormat?: string | undefined;
  themeId?: string | undefined;
  customCss?: string | undefined;
  accessToken?: string | undefined;
  allowedDomains?: string | undefined;
  lastAccessedAt?: string | undefined;
  totalRequests?: number | null | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  venue?: Venue;
  room?: Room;
}

export interface EmbeddingInput {
  name?: string | null | undefined;
  description?: string | null | undefined;
  scheduleType?: string | null | undefined;
  venueId?: string | null | undefined;
  roomId?: string | null | undefined;
  roomTypeId?: string | null | undefined;
  floor?: string | null | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  showPastHours?: number | null | undefined;
  showUpcomingHours?: number | null | undefined;
  layoutType?: string | null | undefined;
  columnsDesktop?: number | null | undefined;
  columnsTablet?: number | null | undefined;
  calendarView?: string | null | undefined;
  calendarStartDay?: number | null | undefined;
  showWeekend?: boolean | null | undefined;
  calendarEventLimit?: number | null | undefined;
  eventTemplateId?: string | null | undefined;
  maxEvents?: number | null | undefined;
  showImages?: boolean | null | undefined;
  showDescriptions?: boolean | null | undefined;
  showLocation?: boolean | null | undefined;
  showRoom?: boolean | null | undefined;
  datetimeFormat?: string | null | undefined;
  themeId?: string | null | undefined;
  customCss?: string | null | undefined;
  accessToken?: string | null | undefined;
  allowedDomains?: string | null | undefined;
  lastAccessedAt?: string | null | undefined;
  totalRequests?: number | null | undefined;
}

export interface EmbeddingFilter {
  id?: string[] | null | undefined;
  scheduleType?: string[] | null | undefined;
  venueId?: string[] | null | undefined;
  roomId?: string[] | null | undefined;
  roomTypeId?: string[] | null | undefined;
  floor?: string[] | null | undefined;
  roomCategoryId?: string[] | null | undefined;
  eventCategoryId?: string[] | null | undefined;
  status?: number[] | null | undefined;
}

export interface EmbeddingWhereInput {
  id?: string | undefined;
}

/**
 * Embedding service that handles all embedding-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EmbeddingService {
  /**
   * Get a list of embeddings with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EmbeddingFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMBEDDING_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single embedding by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMBEDDING_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple embeddings by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMBEDDING_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new embedding
   */
  static async create(params: EmbeddingInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EMBEDDING_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing embedding
   */
  static async update(
    where: EmbeddingWhereInput,
    params: EmbeddingInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(EMBEDDING_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete an embedding
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EMBEDDING_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple embeddings
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EMBEDDING_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
